import { forwardRef } from 'react'
import { cn } from '@/lib/utils'

export type ButtonVariant =
  | 'primary-dark'
  | 'primary-light'
  | 'outline-light'
  | 'outline-dark'
  | 'ghost'

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary-dark', asChild, children, ...props }, ref) => {
    const baseStyles =
      'inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium rounded-lg transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-ink-deep disabled:opacity-50 disabled:cursor-not-allowed'

    const variants: Record<ButtonVariant, string> = {
      'primary-dark':
        'bg-primary text-ink hover:bg-primary-dark',
      'primary-light':
        'bg-ink text-white hover:bg-ink/90',
      'outline-light':
        'border border-white text-white hover:bg-white/10',
      'outline-dark':
        'border border-ink text-ink hover:bg-ink/5',
      'ghost':
        'text-current hover:bg-current/5',
    }

    if (asChild) {
      return (
        <span
          ref={ref as React.Ref<HTMLSpanElement>}
          className={cn(baseStyles, variants[variant], className)}
        >
          {children}
        </span>
      )
    }

    return (
      <button
        ref={ref}
        className={cn(baseStyles, variants[variant], className)}
        {...props}
      >
        {children}
      </button>
    )
  }
)

Button.displayName = 'Button'
